import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, ArrowRight, Sparkles } from "lucide-react";

/**
 * TruthOfTheWeek — homepage teaser pulled from /api/truth-of-the-week.
 * Non-members see the cover + ordinary thing + title + the first line of
 * the lesson, with the rest blurred + a Become-A-Member CTA.
 * Members see the full lesson summary + a deep link into the dashboard.
 */
export default function TruthOfTheWeek() {
  const [rel, setRel] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    http.get("/truth-of-the-week").then(({ data }) => setRel(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!rel) return null;

  const isMember = !!user;
  const summary = rel.lesson_summary || "";
  const previewWords = summary.split(/\s+/).slice(0, 12).join(" ");
  const hasMore = summary.split(/\s+/).length > 12;

  return (
    <section className="bg-[#050505] py-16 md:py-24 border-t border-white/5" data-testid="truth-of-week">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] inline-flex items-center gap-2">
            <Sparkles size={13}/> THIS WEEK'S TRUTH
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-[#F4F1EA] tracking-wide mt-4">
            ORDINARY THING. EXTRAORDINARY LESSON.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center border border-[#C9A055]/25 bg-gradient-to-br from-[#0c0a08] to-[#050505] p-6 md:p-10">
          {/* Image */}
          <div className="lg:col-span-5">
            {rel.image_url ? (
              <div className="relative aspect-square border border-[#C9A055]/30 overflow-hidden bg-black">
                <img
                  src={resolveImage(rel.image_url)}
                  alt={rel.title}
                  className="w-full h-full object-cover"
                  data-testid="totw-image"
                />
                <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end pointer-events-none">
                  <div className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055] bg-black/70 px-3 py-1 backdrop-blur-sm">
                    {(rel.ordinary_thing || "TRUTH").toUpperCase()}
                  </div>
                </div>
              </div>
            ) : (
              <div className="aspect-square border border-[#C9A055]/30 bg-black flex items-center justify-center">
                <span className="font-display text-5xl text-[#C9A055]/30">{(rel.ordinary_thing || "?").slice(0,1)}</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="lg:col-span-7">
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">
              {(rel.ordinary_thing || "AN ORDINARY THING").toUpperCase()}
            </div>
            <h3 className="font-display text-4xl md:text-6xl text-[#F4F1EA] leading-[1.02]" data-testid="totw-title">
              {rel.title}
            </h3>

            {(rel.emotion_tags || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {rel.emotion_tags.slice(0, 4).map((t) => (
                  <span key={t} className="px-2.5 py-1 border border-[#C9A055]/30 text-[#C9A055] font-ui text-[10px] tracking-[0.22em] uppercase">{t}</span>
                ))}
              </div>
            )}

            {/* Lesson preview — blurred for non-members */}
            <div className="mt-7">
              <div className="font-script text-[#C9A055] text-2xl md:text-3xl mb-3">The lesson —</div>
              {isMember ? (
                <p className="font-body text-[16px] md:text-[17px] text-[#F4F1EA] leading-relaxed" data-testid="totw-lesson-full">{summary}</p>
              ) : (
                <div className="relative">
                  <p className="font-body text-[16px] md:text-[17px] text-[#F4F1EA] leading-relaxed" data-testid="totw-lesson-preview">
                    {previewWords}{hasMore && "…"}
                  </p>
                  {hasMore && (
                    <p className="font-body text-[16px] text-[#F4F1EA]/40 leading-relaxed select-none mt-2 blur-sm" aria-hidden="true">
                      {summary.split(/\s+/).slice(12).join(" ") || "The rest of the lesson is reserved for members. Become one — and meet every truth, every week."}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="mt-7 flex flex-wrap gap-3">
              {isMember ? (
                <Link to="/account?tab=weekly" className="btn-gold inline-flex items-center gap-2" data-testid="totw-cta-member">
                  Read The Full Story <ArrowRight size={14}/>
                </Link>
              ) : (
                <>
                  <Link to="/subscriptions" className="btn-gold inline-flex items-center gap-2" data-testid="totw-cta-join">
                    <Lock size={13}/> Become A Member To Read The Rest
                  </Link>
                  <Link to="/subscriptions" className="font-ui text-[10px] tracking-[0.32em] text-[#8a857d] hover:text-[#C9A055] inline-flex items-center gap-2 self-center uppercase">
                    See All Tiers <ArrowRight size={12}/>
                  </Link>
                </>
              )}
            </div>

            {!isMember && (
              <p className="font-body text-[12px] text-[#8a857d] mt-4 italic">
                From $10/mo — a weekly reminder for the people still climbing.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
